import type { ApiProblem, HoApi, StoreApi } from './index';
import type { components as HoComponents } from './generated/ho';
import type { components } from './generated/store';

type Schemas = components['schemas'];

export type CountryLookup = Schemas['CountryLookupDto'];
export type LevelLookup = Schemas['LevelLookupDto'];
export type DivisionLookup = Schemas['DivisionLookupDto'];
export type PostalCodeLookup = Schemas['PostalCodeLookupDto'];
export type LocationDatasetInfo = HoComponents['schemas']['DatasetInfoDto'];
export type LocationImportResult = HoComponents['schemas']['ImportResultDto'];

/** Thrown by every call that fails: the backend's problem with its status. */
function unwrap<T>({ data, error, response }: { data?: T; error?: unknown; response: Response }): T {
  if (!response.ok) {
    const problem = (error ?? {}) as ApiProblem;
    throw { ...problem, status: problem.status ?? response.status } satisfies ApiProblem;
  }
  return data as T;
}

export interface DivisionQuery {
  country?: string;
  level?: number;
  parentId?: string;
  search?: string;
  limit?: number;
}

/**
 * The lists every address form picks from (ADR 0026): countries with their levels, divisions by level and parent,
 * postal codes. The same calls at HO and at a store, where they read the synced replica, so they work offline.
 */
export function createLocationLookup(client: object) {
  const api = client as StoreApi;
  return {
    countries: async () => unwrap(await api.GET('/api/v1/locations/lookup/countries')),
    divisions: async (query: DivisionQuery) =>
      unwrap(await api.GET('/api/v1/locations/lookup/divisions', { params: { query } })),
    postalCodes: async (query: { country?: string; divisionId?: string }) =>
      unwrap(await api.GET('/api/v1/locations/lookup/postal-codes', { params: { query } })),
  };
}

export type LocationLookup = ReturnType<typeof createLocationLookup>;

type Texts = Partial<Record<string, string>>;

/** A country with its levels, as the address form wants it (numbers as numbers, translations as a map). */
export interface AddressCountryOption {
  code: string;
  name: string;
  nameI18n: Texts;
  levels: { level: number; code: string; name: string; nameI18n: Texts; isRequired: boolean }[];
}

export interface AddressDivisionOption {
  id: string;
  code: string;
  level: number;
  name: string;
  nameI18n: Texts;
}

/** The lists the shared address form (design-system AddressFields) reads, from the location master data. */
export function createAddressSource(client: object) {
  const lookup = createLocationLookup(client);
  const texts = (value: unknown): Texts => (value && typeof value === 'object' ? (value as Texts) : {});
  return {
    countries: async (): Promise<AddressCountryOption[]> =>
      (await lookup.countries()).map((c) => ({
        code: c.code,
        name: c.name,
        nameI18n: texts(c.nameI18n),
        levels: c.levels.map((l) => ({
          level: Number(l.level),
          code: l.code,
          name: l.name,
          nameI18n: texts(l.nameI18n),
          isRequired: l.isRequired,
        })),
      })),
    divisions: async (query: {
      country: string;
      level: number;
      parentId?: string;
      limit?: number;
    }): Promise<AddressDivisionOption[]> =>
      (await lookup.divisions(query)).map((d) => ({
        id: d.id,
        code: d.code,
        level: Number(d.level),
        name: d.name,
        nameI18n: texts(d.nameI18n),
      })),
  };
}

/** Importing location datasets, at HO (FR-LOC-05, FR-LOC-06). */
export function createLocationAdminApi(client: object) {
  const api = client as HoApi;
  return {
    datasets: async () => unwrap(await api.GET('/api/v1/locations/datasets')),
    importDataset: async (code: string) =>
      unwrap(await api.POST('/api/v1/locations/datasets/{code}/import', { params: { path: { code } } })),
    /** A dataset file of the built-in format, parsed already. */
    importFile: async (dataset: unknown) =>
      unwrap(await api.POST('/api/v1/locations/import', { body: dataset as never })),
  };
}
